import { getNative } from '../../lib/native';
import { Link } from 'react-router';
import { DataSourcesPanel } from './DataSourcesPanel';
import { SupportContactLink } from './LegalPage';
import { AccountDeletionPanel, type AccountDeletionPreview, type AccountDeletionResult } from './AccountDeletionPanel';
import { request } from '../../lib/api';
import { markOutbox } from '../../features/markers/useMark';
import { locationSharing, nativeMissionSync } from '../../lib/geolocation';
import { compass } from '../../lib/compass';
import { useMission } from '../../state/mission';
import { useEffect, useState } from 'react';
import type { CalloutPrefs } from '@apos/shared';
import { AppHeader } from '../AppHeader';
import { Button, Chip, Field, Icon, Panel, Select, TextInput, Toggle } from '../../components/ui';
import { Notice } from './pageParts';
import { api, errorMessage } from '../../lib/api';
import { retryPrefs, usePrefs } from '../../lib/prefs';
import { useSession } from '../../state/session';
import { VoicePicker } from '../../components/SpokenUpdatesSetting';
import { BlockedUsersPanel } from '../../features/moderation/BlockedUsersPanel';

export function SettingsPage() {
  const user = useSession((s) => s.user)!;
  const prefs = usePrefs();
  const [name, setName] = useState(user.displayName);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [callouts, setCallouts] = useState<CalloutPrefs | null>(null);
  const [calloutBusy, setCalloutBusy] = useState(false);
  useEffect(() => { let active = true; api.calloutPrefs.get().then((p) => { if (active) setCallouts(p); }).catch((e) => { if (active) setError(errorMessage(e)); }); return () => { active = false; }; }, []);
  const saveName = async () => {
    setBusy(true); setError(null); setMessage('');
    try { const { user } = await api.profile.update(name); useSession.setState({ user }); setName(user.displayName); setMessage('Name saved.'); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const saveCallouts = async (patch: Partial<CalloutPrefs>) => {
    setCalloutBusy(true); setError(null);
    try { setCallouts(await api.calloutPrefs.set(patch)); } catch (e) { setError(errorMessage(e)); } finally { setCalloutBusy(false); }
  };
  useEffect(() => { const h = window.location.hash.slice(1); if (h) window.setTimeout(() => document.getElementById(h)?.scrollIntoView({ block: 'start' }), 50); }, []);
  const sections: Array<[string, string]> = [
    ['profile', 'Profile'],
    ['aircraft-data', 'Aircraft data'],
    ['organization', 'Organization'],
    ['appearance', 'Appearance & map'],
    ['on-a-shoot', 'On a shoot'],
    ['account', 'Privacy & account'],
  ];
  const sync = prefs.syncStatus === 'saving' ? 'Saving preferences…' : prefs.syncStatus === 'offline' ? 'Saved on this device. Account sync needs a connection.' : 'Preferences saved';
  return (
    <div className="page">
      <AppHeader back={{ to: '/missions', label: 'Shoots' }} />
      <main className="page__main settings-page">
        <div className="page__head">
          <div>
            <div className="eyebrow">Your account</div>
            <h1>My settings</h1>
            <p className="page__lede">Your preferences follow you across devices.</p>
          </div>
          <div aria-live="polite" className="settings-status">
            <Chip size="sm" tone={prefs.syncStatus === 'offline' ? 'aging' : 'neutral'} icon={prefs.syncStatus === 'saving' ? 'refresh' : prefs.syncStatus === 'offline' ? 'wifi-off' : 'check'}>
              {sync}
            </Chip>
            {prefs.syncStatus === 'offline' ? (
              <Button size="sm" variant="secondary" onClick={() => void retryPrefs()}>
                Retry sync
              </Button>
            ) : null}
          </div>
        </div>
        <div className="settings-layout">
          <nav className="settings-nav" aria-label="Settings sections">
            {sections.map(([id, label]) => (
              <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); history.replaceState(null, '', `#${id}`); }}>
                {label}
              </a>
            ))}
          </nav>
          <div className="settings-body">
            {error ? (
              <Notice tone="error" role="alert">
                {error}
              </Notice>
            ) : null}

            <div className="settings-section" id="profile">
              <Panel title="Profile" eyebrow="You">
                <form className="panel-stack" onSubmit={(e) => { e.preventDefault(); void saveName(); }}>
                  <div className="profile-id">
                    <span className="profile-id__avatar" aria-hidden>{(user.displayName || user.email).trim().charAt(0).toUpperCase()}</span>
                    <div>
                      <div className="profile-id__name">{user.displayName}</div>
                      <div className="profile-id__email">{user.email}</div>
                    </div>
                  </div>
                  <div className="inline-form">
                    <Field label="Display name" hint="Shown to your crew on shoots and in messages.">
                      {(id) => <TextInput id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
                    </Field>
                    <Button type="submit" variant={name.trim() && name.trim() !== user.displayName ? 'primary' : 'secondary'} loading={busy} disabled={!name.trim() || name.trim() === user.displayName}>
                      Save name
                    </Button>
                  </div>
                  {message ? <Notice tone="success" role="status">{message}</Notice> : null}
                </form>
              </Panel>
              <Panel title="Getting started" eyebrow="Setup guide">
                <div className="settings-row">
                  <p className="muted">Join or create an organization, connect aircraft data, and learn the field tools.</p>
                  <Link to="/onboarding" className="btn btn--secondary btn--md">
                    <span className="btn__label">Open setup guide</span>
                    <Icon name="arrow-right" size={16} />
                  </Link>
                </div>
              </Panel>
            </div>

            <div className="settings-section" id="aircraft-data">
              <DataSourcesPanel />
            </div>
            <div className="settings-section">
              <section id="organization"><Panel title="Organization" eyebrow="Team workspace"><p className="muted">Invite people and manage your team’s sources, fleet and rules.</p><Link className="btn btn--secondary" to="/organization">Manage organization</Link></Panel></section>
            </div>

            <div className="settings-section" id="appearance">
              <Panel title="Appearance & map" eyebrow="Display">
                <div className="panel-stack">
                  <div className="settings-grid">
                    <Field label="Theme" hint="Daylight is high-contrast for direct sun.">
                      {(id) => (
                        <Select id={id} value={prefs.theme} onChange={(e) => prefs.set('theme', e.target.value as typeof prefs.theme)}>
                          <option value="dark">Dark</option>
                          <option value="daylight">Daylight</option>
                        </Select>
                      )}
                    </Field>
                    <Field label="Map style">
                      {(id) => (
                        <Select id={id} value={prefs.basemap} onChange={(e) => prefs.set('basemap', e.target.value as typeof prefs.basemap)}>
                          <option value="dark">Dark</option>
                          <option value="daylight">Daylight</option>
                          <option value="satellite">Imagery (US only)</option>
                        </Select>
                      )}
                    </Field>
                    <Field label="Units" hint="Fleets use these units. Shoot instruments use aviation units.">
                      {(id) => (
                        <Select id={id} value={prefs.units} onChange={(e) => prefs.set('units', e.target.value as typeof prefs.units)}>
                          <option value="aviation">Aviation · NM, kt, ft</option>
                          <option value="metric">Metric · km, km/h, m</option>
                        </Select>
                      )}
                    </Field>
                    <Field label="Default live view" hint="The map view a live shoot opens in.">
                      {(id) => (
                        <Select id={id} value={prefs.liveMode ?? ''} onChange={(e) => prefs.set('liveMode', e.target.value || null)}>
                          <option value="">Automatic for my role</option>
                          <option value="track">Track</option>
                          <option value="coverage">Coverage</option>
                          <option value="team">Team</option>
                          <option value="conditions">Conditions</option>
                          <option value="all">Everything</option>
                        </Select>
                      )}
                    </Field>
                    <Field label="Recent trail" hint="How much of each aircraft’s recent path the map draws.">
                      {(id) => (
                        <Select id={id} value={prefs.trailMinutes} onChange={(e) => prefs.set('trailMinutes', Number(e.target.value))}>
                          {[1, 5, 10, 30, 60, 120].map((n) => (
                            <option key={n} value={n}>
                              {n} min
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                  </div>
                  <div className="toggle-list">
                    <Toggle checked={prefs.trackUp} onChange={(v) => prefs.set('trackUp', v)} label="Follow aircraft heading on the map" />
                    <Toggle checked={prefs.showFlightPath} onChange={(v) => prefs.set('showFlightPath', v)} label="Show full recorded flight path" />
                    <Toggle checked={prefs.showTraffic} onChange={(v) => prefs.set('showTraffic', v)} label="Show surrounding public traffic" />
                  </div>
                </div>
              </Panel>
            </div>

            <div className="settings-section" id="on-a-shoot">
              <Panel title="On a shoot" eyebrow="Field">
                <div className="panel-stack">
                  <div className="toggle-list">
                    <div className="toggle-list__item">
                      <Toggle checked={prefs.shareLocation} onChange={(v) => prefs.set('shareLocation', v)} label="Share my location while my shoot is live" />
                      <p className="toggle-list__hint">Location sharing is limited to live shoots you join. Opening Aircraft never starts phone location collection. Device location permission is still required.</p>
                    </div>
                    <Toggle checked={prefs.muteAlerts} onChange={(v) => prefs.set('muteAlerts', v)} label="Mute alert sounds, browser and iPhone notifications" />
                    {callouts ? (
                      <Toggle checked={callouts.enabled} disabled={calloutBusy} onChange={(enabled) => void saveCallouts({ enabled })} label="Speak updates during live shoots (producer calls, takeoff, distance)" />
                    ) : null}
                  </div>
                  {callouts ? (
                    <div className="settings-grid settings-voice">
                      <Field label="Spoken update interval">
                        {(id) => (
                          <Select id={id} disabled={calloutBusy} value={callouts.periodicSec} onChange={(e) => void saveCallouts({ periodicSec: Number(e.target.value) as CalloutPrefs['periodicSec'] })}>
                            <option value="0">Events only</option>
                            <option value="30">Every 30 seconds</option>
                            <option value="60">Every minute</option>
                            <option value="120">Every 2 minutes</option>
                          </Select>
                        )}
                      </Field>
                      <VoicePicker testText="Heron, taxiing." />
                    </div>
                  ) : null}
                </div>
              </Panel>
            </div>

            <div className="settings-section" id="account">
              <Panel title="Privacy and support" eyebrow="Privacy">
                <div className="settings-row">
                  <p className="muted">Your location is shared only while you enable sharing on a live shoot. No advertising tracking.</p>
                  <p className="muted">Support: <SupportContactLink testId="settings-support-contact" /></p>
                  <div className="settings-links">
                    <a className="btn btn--ghost btn--sm" href="/privacy"><span className="btn__label">Privacy policy</span></a>
                    <a className="btn btn--ghost btn--sm" href="/support" data-testid="settings-support-link"><span className="btn__label">Support &amp; reporting</span></a>
                    <a className="btn btn--ghost btn--sm" href="/credits"><span className="btn__label">Credits &amp; licences</span></a>
                  </div>
                </div>
              </Panel>
              <BlockedUsersPanel />
              <AccountDeletionPanel preview={()=>request<AccountDeletionPreview>('GET','/account/deletion')} deleteAccount={body=>request<AccountDeletionResult>('POST','/account/deletion',body)} onDeleted={async()=>{
                nativeMissionSync.update({missionId:null,live:false,sharing:false});
                getNative()?.clearAccountData?.();
                await locationSharing.stop();compass.disable();useMission.getState().unload();markOutbox.clear();
                for(const k of Object.keys(localStorage)) if(k.startsWith('apos.')||k.startsWith('sightline.'))localStorage.removeItem(k);
                await useSession.getState().logout();window.location.replace('/login');
              }}/>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
